import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FaCheckCircle,
  FaClock,
  FaCode,
  FaCopy,
  FaEdit,
  FaEuroSign,
  FaEye,
  FaLayerGroup,
  FaLink,
  FaMoneyCheckAlt,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Select } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { Badge } from 'components/ui/badge';

const DEFAULT_SERVICE = {
  nombre: '',
  descripcion: '',
  duracion: '15',
  es_de_pago: false,
  precio: '0',
};

const DEFAULT_WIDGET_CONFIG = {
  title: 'Reserva tu cita',
  subtitle: 'Selecciona servicio y horario',
  showReasonField: true,
  hideServiceField: false,
  serviceRequired: false,
  preselectedServiceId: '',
  reasons: ['Demo', 'Primera visita', 'Seguimiento'],
  paymentsEnabled: false,
  publishPlans: false,
  plans: [
    { id: 'starter', name: 'Starter', price: 49, period: 'mes', description: 'Para empezar', highlighted: false },
    { id: 'growth', name: 'Growth', price: 99, period: 'mes', description: 'El más popular', highlighted: true },
    { id: 'scale', name: 'Scale', price: 199, period: 'mes', description: 'Para equipos grandes', highlighted: false },
  ],
};

function Servicios() {
  const [servicios, setServicios] = useState([]);
  const [error, setError] = useState('');
  const [newServicio, setNewServicio] = useState(DEFAULT_SERVICE);
  const [editingServicio, setEditingServicio] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bookingSlug, setBookingSlug] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [savingBookingConfig, setSavingBookingConfig] = useState(false);
  const [bookingConfig, setBookingConfig] = useState(DEFAULT_WIDGET_CONFIG);
  const previewMountRef = useRef(null);

  const totalServicios = servicios.length;
  const paidServicios = servicios.filter((s) => s.es_de_pago).length;
  const promedioDuracion = useMemo(() => {
    if (!servicios.length) return 0;
    const total = servicios.reduce((sum, s) => sum + Number(s.duracion || 0), 0);
    return Math.round(total / servicios.length);
  }, [servicios]);

  const getAuthToken = () => sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');

  const getBookingUrl = () => {
    if (!bookingSlug) return '';
    return `${window.location.origin}/api/public/booking/${bookingSlug}`;
  };

  const getWidgetSnippet = () => {
    if (!bookingSlug) return '';
    const attrs = [
      `src="${window.location.origin}/booking-widget.js"`,
      `data-slug="${bookingSlug}"`,
      `data-api-base="${window.location.origin}"`,
      `data-title="${bookingConfig.title || 'Reserva tu cita'}"`,
      `data-subtitle="${bookingConfig.subtitle || 'Selecciona servicio y horario'}"`,
      `data-show-reason="${bookingConfig.showReasonField ? 'true' : 'false'}"`,
      `data-hide-service="${bookingConfig.hideServiceField ? 'true' : 'false'}"`,
      `data-service-required="${bookingConfig.serviceRequired ? 'true' : 'false'}"`,
      `data-reasons="${(bookingConfig.reasons || []).join(',')}"`,
    ];
    if (bookingConfig.preselectedServiceId) {
      attrs.push(`data-service-id="${bookingConfig.preselectedServiceId}"`);
    }
    return `<script ${attrs.join(' ')}></script>`;
  };

  const getWidgetPreviewUrl = () => {
    if (!bookingSlug) return '';
    const params = new URLSearchParams({
      slug: bookingSlug,
      apiBase: window.location.origin,
      title: bookingConfig.title || 'Reserva tu cita',
      subtitle: bookingConfig.subtitle || 'Selecciona servicio y horario',
      hideService: bookingConfig.hideServiceField ? 'true' : 'false',
      serviceRequired: bookingConfig.serviceRequired ? 'true' : 'false',
      reasons: (bookingConfig.reasons || []).join(','),
    });
    if (bookingConfig.preselectedServiceId) {
      params.set('serviceId', bookingConfig.preselectedServiceId);
    }
    return `${window.location.origin}/booking-preview.html?${params.toString()}`;
  };

  const copyToClipboard = async (value, label) => {
    try {
      if (!value) {
        setError(`No hay ${label.toLowerCase()} para copiar`);
        return;
      }
      await navigator.clipboard.writeText(value);
      setCopyMessage(`${label} copiado`);
      setTimeout(() => setCopyMessage(''), 2200);
    } catch (err) {
      setError(`No se pudo copiar ${label.toLowerCase()}`);
    }
  };

  async function fetchServicios() {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró el token de autenticación');

      const response = await axios.get('/api/servicios', {
        headers: { Authorization: `Bearer ${token}` },
      });

      setServicios(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      setError(`Error al cargar servicios: ${err.response?.data?.error || err.message}`);
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    if (editingServicio) {
      setEditingServicio({ ...editingServicio, [name]: nextValue });
    } else {
      setNewServicio({ ...newServicio, [name]: nextValue });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró el token de autenticación');

      const servicioToSubmit = editingServicio || newServicio;
      const payload = {
        nombre: servicioToSubmit.nombre,
        descripcion: servicioToSubmit.descripcion,
        duracion: servicioToSubmit.duracion,
        es_de_pago: servicioToSubmit.es_de_pago,
        precio: servicioToSubmit.es_de_pago ? servicioToSubmit.precio : '0',
      };

      if (editingServicio) {
        const response = await axios.put(`/api/servicios/${editingServicio.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setServicios((prev) => prev.map((s) => (s.id === editingServicio.id ? response.data : s)));
      } else {
        const response = await axios.post('/api/servicios', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setServicios((prev) => [...prev, response.data]);
      }

      setNewServicio(DEFAULT_SERVICE);
      setEditingServicio(null);
      setIsModalOpen(false);
      setError('');
    } catch (err) {
      setError(`Error al guardar servicio: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró el token de autenticación');

      await axios.delete(`/api/servicios/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServicios((prev) => prev.filter((servicio) => servicio.id !== id));
      setError('');
    } catch (err) {
      setError(`Error al eliminar servicio: ${err.response?.data?.error || err.message}`);
    }
  };

  const generatePaymentLink = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró el token de autenticación');

      const response = await axios.post(
        `/api/servicios/${id}/payment-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.paymentLink, '_blank');
    } catch (err) {
      setError(`Error al generar enlace de pago: ${err.response?.data?.error || err.message}`);
    }
  };

  const openModal = (servicio = null) => {
    if (servicio) {
      setEditingServicio(servicio);
    } else {
      setEditingServicio(null);
      setNewServicio(DEFAULT_SERVICE);
    }
    setIsModalOpen(true);
  };

  const fetchBookingSlug = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await axios.get('/api/usuarios/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookingSlug(response.data?.radicale_username || '');
    } catch (err) {
      console.error('Error al obtener slug de reserva:', err);
    }
  };

  const fetchBookingConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await axios.get('/api/servicios/booking-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const cfg = response.data?.configuracion || {};
      setBookingConfig((prev) => ({
        ...prev,
        ...cfg,
        showReasonField: typeof cfg.showReasonField === 'boolean' ? cfg.showReasonField : prev.showReasonField,
        hideServiceField: typeof cfg.hideServiceField === 'boolean' ? cfg.hideServiceField : prev.hideServiceField,
        reasons: Array.isArray(cfg.reasons) ? cfg.reasons : prev.reasons,
        paymentsEnabled: typeof cfg.paymentsEnabled === 'boolean' ? cfg.paymentsEnabled : prev.paymentsEnabled,
        publishPlans: typeof cfg.publishPlans === 'boolean' ? cfg.publishPlans : prev.publishPlans,
        plans: Array.isArray(cfg.plans) && cfg.plans.length > 0 ? cfg.plans : prev.plans,
      }));
    } catch (err) {
      console.error('Error al obtener configuración del widget:', err);
    }
  };

  const saveBookingConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró token de autenticación');

      setSavingBookingConfig(true);
      await axios.put(
        '/api/servicios/booking-config',
        { activo: true, configuracion: bookingConfig },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCopyMessage('Configuración guardada');
      setTimeout(() => setCopyMessage(''), 2200);
    } catch (err) {
      setError(`No se pudo guardar configuración: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingBookingConfig(false);
    }
  };

  useEffect(() => {
    fetchServicios();
    fetchBookingSlug();
    fetchBookingConfig();
  }, []);

  useEffect(() => {
    if (!bookingSlug || !previewMountRef.current) return;

    const mount = previewMountRef.current;
    mount.innerHTML = '';

    const script = document.createElement('script');
    script.src = `${window.location.origin}/booking-widget.js`;
    script.setAttribute('data-slug', bookingSlug);
    script.setAttribute('data-api-base', window.location.origin);
    script.setAttribute('data-title', bookingConfig.title || 'Reserva tu cita');
    script.setAttribute('data-subtitle', bookingConfig.subtitle || 'Selecciona servicio y horario');
    script.setAttribute('data-show-reason', bookingConfig.showReasonField ? 'true' : 'false');
    script.setAttribute('data-hide-service', bookingConfig.hideServiceField ? 'true' : 'false');
    script.setAttribute('data-service-required', bookingConfig.serviceRequired ? 'true' : 'false');
    script.setAttribute('data-reasons', (bookingConfig.reasons || []).join(','));
    if (bookingConfig.preselectedServiceId) {
      script.setAttribute('data-service-id', bookingConfig.preselectedServiceId);
    }

    mount.appendChild(script);

    return () => {
      mount.innerHTML = '';
    };
  }, [bookingSlug, bookingConfig]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div
        className="mb-6 rounded-2xl p-6 text-white shadow-lg"
        style={{ background: 'linear-gradient(105deg, #020617 0%, #0f172a 55%, #1d4ed8 100%)' }}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="flex items-center text-3xl font-bold">
              <FaLayerGroup className="mr-3 text-blue-200" />
              Servicios y Formularios
            </h1>
            <p className="mt-1 text-sm text-blue-100">
              Define tu catálogo y publica tu widget de reservas en web y Google.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => openModal()}
              variant="success"
              className="rounded-xl border border-emerald-300/30 bg-emerald-500 text-white hover:bg-emerald-400"
            >
              <FaPlus className="mr-2 text-emerald-100" />
              Nuevo servicio
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="rounded-2xl p-4">
          <p className="flex items-center text-xs font-semibold uppercase tracking-wide text-slate-500">
            <FaLayerGroup className="mr-2 text-slate-500" /> Servicios
          </p>
          <p className="mt-2 text-3xl font-bold text-slate-900">{totalServicios}</p>
        </Card>
        <Card className="rounded-2xl border-emerald-200 bg-emerald-50 p-4">
          <p className="flex items-center text-xs font-semibold uppercase tracking-wide text-emerald-700">
            <FaMoneyCheckAlt className="mr-2 text-emerald-700" /> De pago
          </p>
          <p className="mt-2 text-3xl font-bold text-emerald-800">{paidServicios}</p>
        </Card>
        <Card className="rounded-2xl border-blue-200 bg-blue-50 p-4">
          <p className="flex items-center text-xs font-semibold uppercase tracking-wide text-blue-700">
            <FaClock className="mr-2 text-blue-700" /> Duración media
          </p>
          <p className="mt-2 text-3xl font-bold text-blue-900">{promedioDuracion} min</p>
        </Card>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      {copyMessage ? (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{copyMessage}</div>
      ) : null}

      <Card className="mb-6 rounded-2xl border-blue-200 p-5">
        <div className="mb-4">
          <h2 className="flex items-center text-xl font-semibold text-slate-900">
            <FaCode className="mr-2 text-blue-700" />
            Publicador de formulario
          </h2>
          <p className="text-sm text-slate-500">
            Configura el widget, revisa la previsualización y copia el código para tu web.
          </p>
        </div>

        {!bookingSlug ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
            No se pudo resolver tu slug público todavía.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card className="rounded-xl bg-slate-50 p-4">
              <p className="mb-3 text-sm font-semibold text-slate-800">Configuración del widget</p>

              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Título</label>
                  <Input
                    type="text"
                    value={bookingConfig.title}
                    onChange={(e) => setBookingConfig((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Subtítulo</label>
                  <Input
                    type="text"
                    value={bookingConfig.subtitle}
                    onChange={(e) => setBookingConfig((prev) => ({ ...prev, subtitle: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="mb-1 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={bookingConfig.showReasonField}
                      onChange={(e) =>
                        setBookingConfig((prev) => ({ ...prev, showReasonField: e.target.checked }))
                      }
                    />
                    Mostrar campo "Tipo de cita"
                  </label>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Tipos de cita (coma separadas)</label>
                  <Input
                    type="text"
                    disabled={!bookingConfig.showReasonField}
                    value={(bookingConfig.reasons || []).join(', ')}
                    onChange={(e) =>
                      setBookingConfig((prev) => ({
                        ...prev,
                        reasons: e.target.value.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 8),
                      }))
                    }
                  />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={bookingConfig.hideServiceField}
                      onChange={(e) =>
                        setBookingConfig((prev) => ({
                          ...prev,
                          hideServiceField: e.target.checked,
                          serviceRequired: e.target.checked ? false : prev.serviceRequired,
                        }))
                      }
                    />
                    Ocultar selector de servicio (modo demo/contacto)
                  </label>
                  <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={bookingConfig.serviceRequired}
                      disabled={bookingConfig.hideServiceField}
                      onChange={(e) =>
                        setBookingConfig((prev) => ({ ...prev, serviceRequired: e.target.checked }))
                      }
                    />
                    Servicio obligatorio
                  </label>
                  <Select
                    value={bookingConfig.preselectedServiceId}
                    onChange={(e) =>
                      setBookingConfig((prev) => ({ ...prev, preselectedServiceId: e.target.value }))
                    }
                  >
                    <option value="">Sin servicio preseleccionado</option>
                    {servicios.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nombre}
                      </option>
                    ))}
                  </Select>
                </div>

                <Button
                  onClick={saveBookingConfig}
                  disabled={savingBookingConfig}
                >
                  <FaCheckCircle className="mr-2" />
                  {savingBookingConfig ? 'Guardando...' : 'Guardar configuración'}
                </Button>
              </div>
            </Card>

            <Card className="h-full rounded-xl bg-slate-50 p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="flex items-center text-sm font-semibold text-slate-800">
                  <FaEye className="mr-2 text-slate-600" />
                  Previsualización en vivo
                </p>
                <a
                  href={getWidgetPreviewUrl()}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-blue-700 hover:text-blue-800"
                >
                  Abrir grande
                </a>
              </div>
              <div className="h-full min-h-[560px] w-full overflow-auto rounded-lg border border-slate-300 bg-white md:min-h-[680px] xl:min-h-[780px]">
                <div ref={previewMountRef} className="p-3" />
              </div>
            </Card>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Card className="rounded-xl bg-slate-50 p-3">
            <p className="mb-1 flex items-center text-xs font-semibold text-slate-500">
              <FaLink className="mr-2 text-blue-600" />
              URL pública (Google Business Profile)
            </p>
            <div className="flex gap-2">
              <Input
                readOnly
                value={getBookingUrl()}
                className="flex-1"
              />
              <Button
                onClick={() => copyToClipboard(getBookingUrl(), 'Enlace')}
                variant="primary"
              >
                <FaCopy className="mr-2" />
                Copiar
              </Button>
            </div>
          </Card>

          <Card className="rounded-xl bg-slate-50 p-3">
            <p className="mb-1 flex items-center text-xs font-semibold text-slate-500">
              <FaCode className="mr-2 text-indigo-600" />
              Snippet widget para web
            </p>
            <div className="flex gap-2">
              <Textarea
                readOnly
                rows={3}
                value={getWidgetSnippet()}
                className="flex-1 text-xs"
              />
              <Button
                onClick={() => copyToClipboard(getWidgetSnippet(), 'Snippet')}
                variant="primary"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <FaCopy className="mr-2" />
                Copiar
              </Button>
            </div>
          </Card>
        </div>
      </Card>

      <Card className="rounded-2xl p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center text-xl font-semibold text-slate-900">
            <FaLayerGroup className="mr-2 text-slate-700" />
            Catálogo de servicios
          </h2>
          <Button
            onClick={() => openModal()}
            variant="success"
          >
            <FaPlus className="mr-2" />
            Añadir servicio
          </Button>
        </div>

        {servicios.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
            Aún no tienes servicios. Crea el primero para empezar a aceptar reservas.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {servicios.map((servicio) => (
              <Card key={servicio.id} className="rounded-xl bg-gradient-to-b from-white to-slate-50 p-4">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <h3 className="text-lg font-semibold text-slate-900">{servicio.nombre}</h3>
                  {servicio.es_de_pago ? (
                    <Badge variant="success">Pago</Badge>
                  ) : (
                    <Badge>Gratis</Badge>
                  )}
                </div>

                <p className="mb-4 line-clamp-3 text-sm text-slate-600">{servicio.descripcion}</p>

                <div className="mb-4 space-y-2 text-sm">
                  <div className="flex items-center text-slate-700">
                    <FaClock className="mr-2 text-slate-500" />
                    {servicio.duracion} minutos
                  </div>
                  {servicio.es_de_pago ? (
                    <div className="flex items-center text-emerald-700">
                      <FaEuroSign className="mr-2" />
                      {Number(servicio.precio || 0).toFixed(2)}
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => openModal(servicio)}
                    variant="secondary"
                    size="sm"
                  >
                    <FaEdit className="mr-2" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleDelete(servicio.id)}
                    variant="danger"
                    size="sm"
                  >
                    <FaTrash className="mr-2" />
                    Eliminar
                  </Button>
                  {servicio.es_de_pago ? (
                    <Button
                      onClick={() => generatePaymentLink(servicio.id)}
                      variant="success"
                      size="sm"
                    >
                      <FaLink className="mr-2 text-emerald-700" />
                      Enlace cobro
                    </Button>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-2xl font-bold text-slate-900">
              {editingServicio ? 'Editar servicio' : 'Nuevo servicio'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Nombre</label>
                <Input
                  type="text"
                  name="nombre"
                  value={editingServicio ? editingServicio.nombre : newServicio.nombre}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Descripción</label>
                <Textarea
                  name="descripcion"
                  rows={4}
                  value={editingServicio ? editingServicio.descripcion : newServicio.descripcion}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">Duración (min)</label>
                  <Input
                    type="number"
                    name="duracion"
                    min="1"
                    value={editingServicio ? editingServicio.duracion : newServicio.duracion}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    name="es_de_pago"
                    checked={editingServicio ? editingServicio.es_de_pago : newServicio.es_de_pago}
                    onChange={handleInputChange}
                  />
                  Servicio de pago
                </label>
              </div>

              {(editingServicio ? editingServicio.es_de_pago : newServicio.es_de_pago) ? (
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">Precio (€)</label>
                  <Input
                    type="number"
                    name="precio"
                    min="0"
                    step="0.01"
                    value={editingServicio ? editingServicio.precio : newServicio.precio}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingServicio(null);
                  }}
                  variant="secondary"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                >
                  {editingServicio ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

    </div>
  );
}

export default Servicios;
